/* eslint-disable @typescript-eslint/strict-boolean-expressions */
import { useApolloClient } from '@apollo/client';
import { ManagedServiceId } from '@axinom/mosaic-id-utils';
import {
  type ActionData,
  type Column,
  createUpdateGQLFragmentGenerator,
  Details,
  type DetailsProps,
  type FormActionData,
  formatDateTime,
  FormElementContainer,
  getFormDiff,
  ImgElement,
  InfoPanel,
  InfoTooltip,
  List,
  type ObjectSchemaDefinition,
  Paragraph,
  RadioField,
  Section,
  SingleLineTextField,
  type StationMessage,
} from '@axinom/mosaic-ui';
import { config } from 'config';
import { Field, type FormikValues, useFormikContext } from 'formik';
import gql from 'graphql-tag';
import React, { type ReactNode, useCallback, useEffect, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import {
  addEnableDisableActionForService,
  formatEnumValueToTitleCase,
} from 'util/managedServiceUtil';
import * as Yup from 'yup';
import {
  type Environment,
  EnvironmentDocument,
  type EnvironmentOrchestrationAndManagedServiceDataQuery,
  type EnvironmentQuery,
  EnvironmentServiceState,
  type Mutation,
  type MutationUpdateEnvironmentArgs,
  OrchestrationState,
  useDeleteEnvironmentMutation,
  useEnvironmentOrchestrationAndManagedServiceDataQuery,
  useEnvironmentQuery,
} from '../../../../generated/id-service-env-administration';
import {
  DomainEnvironmentMappingDataDocument,
  type DomainEnvironmentMappingDataQuery,
  type PortalUrl,
  PortalUrlType,
} from '../../../../generated/portal-service';
import errorDisable from '../../../../images/status/disable_error.svg';
import disabled from '../../../../images/status/disabled.svg';
import disabling from '../../../../images/status/disabling.svg';
import errorEnable from '../../../../images/status/enable_error.svg';
import enabled from '../../../../images/status/enabled.svg';
import enabled_always from '../../../../images/status/enabled_always.svg';
import enabling from '../../../../images/status/enabling.svg';
import type { BreadcrumbResolver } from '../../../../models/breadcrumb';
import { apolloClient } from '../../../../modules/apolloClient';
import { authStateStore } from '../../../../state-store';
import { environmentApolloClient } from '../../../environmentApolloClient';
import classes from './EnvironmentDetail.module.scss';

interface FormData {
  name?: string;
  enabled?: boolean;
  isDevelopment?: boolean;
  orchestrationState?: OrchestrationState;
  managedServiceStates?: ManagedServiceData[];
}

const environmentDetailSchema = Yup.object<ObjectSchemaDefinition<FormData>>({
  name: Yup.string().required('Name is a required field').max(255),
  enabled: Yup.boolean().required('Environment Status is a required field'),
});

interface ManagedServiceData {
  position: number;
  serviceId: string;
  serviceName: string;
  isCoreService: boolean;
  status: EnvironmentServiceState;
}

const useEnvironmentFormData = (
  environment: EnvironmentQuery['environment'],
  orchestrationResult: EnvironmentOrchestrationAndManagedServiceDataQuery['orchestrationResult'],
  managedServiceData: EnvironmentOrchestrationAndManagedServiceDataQuery['environmentManagedServices'],
): FormData | undefined => {
  const [environmentData, setEnvironmentData] = useState<FormData>();

  useEffect(() => {
    const data: FormData = {
      ...environment,
      ...orchestrationResult,
    };

    data.managedServiceStates =
      managedServiceData?.nodes.map<ManagedServiceData>((managedService) => {
        return {
          position: managedService.sortOrder ?? 0,
          serviceId: managedService.managedServiceId,
          serviceName: managedService.managedServiceName ?? '',
          isCoreService: managedService.isCoreService ?? false,
          status: managedService.state,
        };
      });

    setEnvironmentData(data);
  }, [environment, managedServiceData, orchestrationResult]);

  return environmentData;
};

export const EnvironmentDetail: React.FC = () => {
  const apolloClient = useApolloClient();
  const { envId } = useParams<{
    envId: string;
    action: string;
  }>();

  const [stationMessage, setStationMessage] = useState<StationMessage>();

  const { loading, data, error } = useEnvironmentQuery({
    client: apolloClient,
    variables: { id: envId },
    fetchPolicy: 'no-cache',
  });

  // Display a warning if the requested environment is not found under the current tenant
  const tenantName = authStateStore.useState('tenantName');
  useEffect(() => {
    if (!data?.environment) {
      setStationMessage({
        type: 'warning',
        title: `The requested Environment is not found under the Tenant [${tenantName}]. Please check if you're using the correct Tenant. If not, you can switch the current Tenant using the link in the User Information area.`,
      });
    } else {
      setStationMessage(undefined);
    }
  }, [data?.environment, tenantName]);

  const environmentOrchestrationAndManagedServiceData =
    useEnvironmentOrchestrationAndManagedServiceDataQuery({
      client: apolloClient,
      fetchPolicy: 'no-cache',
      variables: {
        id: envId,
        condition: {
          environmentId: envId,
        },
      },
      //TODO: Replace polling with a GraphQL Subscription for updating service state changes
      pollInterval: data?.environment ? 5000 : 0,
    });

  const environmentData = useEnvironmentFormData(
    data?.environment ?? null,
    environmentOrchestrationAndManagedServiceData.data?.orchestrationResult ??
      null,
    environmentOrchestrationAndManagedServiceData.data
      ?.environmentManagedServices ?? null,
  );

  const [managementSystemUrls, setManagementSystemUrls] = useState<PortalUrl[]>(
    [{ url: 'Pending...', urlType: PortalUrlType.Default }],
  );

  useEffect(() => {
    (async () => {
      // Check orchestration-state
      if (
        environmentOrchestrationAndManagedServiceData.data?.orchestrationResult
          ?.orchestrationState === OrchestrationState.Error
      ) {
        setStationMessage({
          type: 'warning',
          title:
            'An error has occurred during environment initialization. More details can be found on the [Service Log Messages] station. Retrying the environment creation may solve the issue if it was temporary. If the error still persists, please contact Axinom Support.',
        });
      } else {
        setStationMessage(undefined);
      }

      // Get portal subdomain
      const portalServiceEnabled =
        environmentOrchestrationAndManagedServiceData.data?.environmentManagedServices?.nodes.find(
          (service) =>
            service.managedServiceId === ManagedServiceId.PORTAL_SERVICE,
        )?.state;

      if (portalServiceEnabled === EnvironmentServiceState.Enabled) {
        try {
          const envApolloClient = environmentApolloClient(
            envId,
            new URL(`/graphql`, config.portalServiceAdminBaseUrl).toString(),
          );

          const domainEnvironmentMappingDataResult =
            await envApolloClient?.query<DomainEnvironmentMappingDataQuery>({
              query: DomainEnvironmentMappingDataDocument,
              variables: {},
              fetchPolicy: 'no-cache',
            });
          if (
            domainEnvironmentMappingDataResult?.data?.domainEnvironmentMappings
              ?.nodes.length === 1
          ) {
            setManagementSystemUrls(
              domainEnvironmentMappingDataResult.data.domainEnvironmentMappings
                ?.nodes[0].portalUrls || [],
            );
          }
        } catch (_err) {
          setManagementSystemUrls([
            { url: 'Service Unavailable', urlType: PortalUrlType.Default },
          ]);
        }
      } else if (portalServiceEnabled === EnvironmentServiceState.Disabled) {
        setManagementSystemUrls([
          { url: 'Not Available', urlType: PortalUrlType.Default },
        ]);
      } else {
        setManagementSystemUrls([
          { url: 'Pending...', urlType: PortalUrlType.Default },
        ]);
      }
    })();
  }, [envId, environmentOrchestrationAndManagedServiceData]);

  const { actions } = useActions(
    envId,
    data?.environment?.isDevelopment,
    environmentData?.orchestrationState,
  );

  const onSubmit = useCallback(
    async (
      formData: FormData,
      initialData: DetailsProps<FormData>['initialData'],
    ): Promise<void> => {
      if (initialData.loading) {
        return;
      }

      const mutations: string[] = [];

      const generateUpdateGQLFragment =
        createUpdateGQLFragmentGenerator<Mutation>();

      const generateUpdateEnvironmentMutation = (): string =>
        generateUpdateGQLFragment<MutationUpdateEnvironmentArgs>(
          'updateEnvironment',
          {
            input: {
              id: envId,
              patch: createUpdateDto(formData, initialData.data),
            },
          },
        );

      mutations.push(generateUpdateEnvironmentMutation());

      const GqlDoc = gql`mutation UpdateEnvironment {
        ${mutations}
      }`;

      await apolloClient.mutate({
        mutation: GqlDoc,
        fetchPolicy: 'no-cache',
      });
    },
    [apolloClient, envId],
  );

  return (
    <Details<FormData>
      defaultTitle="Environment"
      titleProperty="name"
      subtitle="Environment Details"
      actions={data?.environment ? actions : undefined}
      validationSchema={environmentDetailSchema}
      initialData={{
        data: environmentData,
        loading,
        error: error?.message,
      }}
      saveData={onSubmit}
      infoPanel={
        data?.environment ? (
          <Panel managementSystemUrls={managementSystemUrls} />
        ) : undefined
      }
      stationMessage={stationMessage}
    >
      {data?.environment && environmentData?.managedServiceStates && <Form />}
    </Details>
  );
};

const Panel: React.FC<{ managementSystemUrls: PortalUrl[] }> = ({
  managementSystemUrls,
}) => {
  const { values } = useFormikContext<Environment>();

  return (
    <InfoPanel>
      <Section>
        <Paragraph title="Environment ID">{values.id}</Paragraph>
        <Paragraph title="Created">
          {formatDateTime(values.createdAt)} by {values.createdBy}
        </Paragraph>
        <Paragraph title="Last Modified">
          {formatDateTime(values.updatedAt)} by {values.updatedBy}
        </Paragraph>
        <Paragraph title="Environment Initialization Status">
          {formatEnumValueToTitleCase(values.orchestrationState)}
        </Paragraph>
      </Section>
      <Section>
        <Paragraph title="Management System URLs">
          {managementSystemUrls.map((managementSystemUrl, index) =>
            managementSystemUrl.url.startsWith(`http://`) ||
            managementSystemUrl.url.startsWith(`https://`) ? (
              <div key={index} className={classes.urlDiv}>
                <a
                  href={managementSystemUrl.url}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {managementSystemUrl.url}
                </a>
                {managementSystemUrl.urlType === PortalUrlType.Default && (
                  <InfoTooltip className={classes.urlInfo}>
                    You can change the URL in the &quot;Management System
                    Service&quot; configuration.
                  </InfoTooltip>
                )}
              </div>
            ) : (
              <div key={index}>{managementSystemUrl.url}</div>
            ),
          )}
        </Paragraph>
      </Section>
      <Section>
        <Paragraph title="Tenant ID">{values.tenantId}</Paragraph>
      </Section>
    </InfoPanel>
  );
};

const Form: React.FC = () => {
  return (
    <>
      <Field name="name" label="Name" as={SingleLineTextField} />
      <Field
        name="enabled"
        label="Environment Status"
        options={[
          { value: true, label: 'Enabled' },
          { value: false, label: 'Disabled' },
        ]}
        transform={(value: string) => JSON.parse(value)}
        as={RadioField}
      />
      <Field
        name="isDevelopment"
        label="Environment Type"
        options={[
          { value: true, label: 'Development' },
          { value: false, label: 'Production' },
        ]}
        transform={(value: string) => JSON.parse(value)}
        as={RadioField}
      />
      <ManagedServiceStates />
    </>
  );
};

const ManagedServiceStates: React.FC = () => {
  const history = useHistory();
  const { envId } = useParams<{
    envId: string;
  }>();
  const { values } = useFormikContext<FormData>();
  const managedServices = values.managedServiceStates;

  const generateInlineMenuActions: (
    data: ManagedServiceData,
  ) => ActionData[] = ({ status, serviceId }) => {
    const inlineActions: ActionData[] = [];

    if (status === EnvironmentServiceState.Disabled) {
      addEnableDisableActionForService(
        inlineActions,
        envId,
        serviceId,
        status,
        () => {
          history.push(`${window.location.pathname}`);
        },
      );
    }
    return inlineActions;
  };

  const EnvDetailsStateStatusRenderer = (
    status: unknown,
    data: ManagedServiceData,
  ): ReactNode => {
    let icon;

    if (data.isCoreService === true && status === 'ENABLED') {
      icon = enabled_always;
    } else if (status === 'ENABLED') {
      icon = enabled;
    } else if (status === 'DISABLED') {
      icon = disabled;
    } else if (status === 'ENABLING') {
      icon = enabling;
    } else if (status === 'DISABLING') {
      icon = disabling;
    } else if (status === 'ERROR_ENABLING') {
      icon = errorEnable;
    } else if (status === 'ERROR_DISABLING') {
      icon = errorDisable;
    } else {
      icon = '';
    }

    return (
      <div className={classes.status_container}>
        <ImgElement
          src={icon}
          style={{ width: '20px', height: '20px' }}
          decorative={true}
        />
        <div className={classes.status}>
          {formatEnumValueToTitleCase(status as string)}
        </div>
      </div>
    );
  };

  const columns: Column<ManagedServiceData>[] = [
    {
      propertyName: 'serviceName',
      label: 'Service Name',
      sortable: false,
    },
    {
      propertyName: 'status',
      label: 'Service State',
      render: EnvDetailsStateStatusRenderer,
      size: '150px',
      sortable: false,
    },
  ];

  const managedServicesSortedByPosition = managedServices
    ? managedServices.sort(function (a, b) {
        return a.position - b.position;
      })
    : [];

  const coreServices: ManagedServiceData[] = [];
  const otherServices: ManagedServiceData[] = [];

  managedServicesSortedByPosition
    ?.sort((a, b) =>
      a.serviceName.toLowerCase() < b.serviceName.toLowerCase() ? -1 : 1,
    )
    .forEach((state: ManagedServiceData) => {
      (state.isCoreService ? coreServices : otherServices).push({
        serviceName: state.serviceName,
        status: state.status,
        serviceId: state.serviceId,
        isCoreService: state.isCoreService,
        position: state.position,
      });
    });

  return (
    <>
      <FormElementContainer
        label="Core Services"
        dataTestFieldType="CoreServicesList"
      >
        <List<ManagedServiceData>
          columns={columns}
          data={coreServices}
          generateItemLink={(item) =>
            `/env/${envId}/services/${item.serviceId}/config`
          }
          inlineMenuActions={
            values.orchestrationState === OrchestrationState.Completed
              ? generateInlineMenuActions
              : undefined
          }
          keyProperty={'serviceId'}
        />
      </FormElementContainer>
      <FormElementContainer
        label="Managed Services"
        dataTestFieldType="ManagedServicesList"
      >
        <List<ManagedServiceData>
          columns={columns}
          data={otherServices}
          generateItemLink={(item) =>
            `/env/${envId}/services/${item.serviceId}/config`
          }
          inlineMenuActions={
            values.orchestrationState === OrchestrationState.Completed
              ? generateInlineMenuActions
              : undefined
          }
          keyProperty={'serviceId'}
        />
      </FormElementContainer>
    </>
  );
};

function insertIf<T>(condition: unknown, ...elements: T[]): T[] {
  return condition ? elements : [];
}

function useActions(
  id: string,
  isDevelopment: boolean | null | undefined,
  orchestrationState: OrchestrationState | undefined,
): {
  readonly actions: FormActionData<FormData>[];
} {
  const history = useHistory();
  const apolloClient = useApolloClient();

  const [environmentDelete] = useDeleteEnvironmentMutation({
    client: apolloClient,
    fetchPolicy: 'no-cache',
  });

  const actions: FormActionData<FormData>[] = [
    {
      label: 'Service Accounts',
      path: `/env/${id}/serviceAccounts`,
      isDisabled: orchestrationState !== OrchestrationState.Completed,
    },
    {
      label: 'Service Log Messages',
      path: `/env/${id}/servicesLogMessages`,
    },
    ...insertIf(isDevelopment, {
      label: 'Environment Connection',
      path: `/env/${id}/environmentConnection`,
    }),
    {
      label: 'Delete',
      confirmationMode: 'Advanced',
      confirmationConfig: {
        body: 'The action is irreversible. By confirming the action the current environment is going to be deleted. Please consider disabling it, or confirm to proceed with deletion.',
      },
      onActionSelected: async () => {
        await environmentDelete({ variables: { input: { id } } });
        history.push(`/env`);
      },
    },
  ];

  return { actions } as const;
}

function createUpdateDto(
  currentValues: FormikValues,
  initialValues: FormikValues | undefined | null,
): FormikValues {
  const updateData = getFormDiff(currentValues, initialValues);

  // ServiceStateChanges are handled by the `generateChangeServiceStateMutation` method call inside the onSubmit handler
  delete updateData['managedServiceStates'];

  return updateData;
}

export const EnvironmentDetailCrumb: BreadcrumbResolver = ({ envId }) => {
  return async (): Promise<string> => {
    const response = await apolloClient().query<EnvironmentQuery>({
      query: EnvironmentDocument,
      variables: { id: envId },
      errorPolicy: 'ignore',
    });
    return response.data.environment?.name || 'Environments Details';
  };
};
